import { HttpResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, interval, Subject, switchMap, take, takeUntil, takeWhile, timeout } from 'rxjs';
import { BasicMachineFunctionsService, t_dataName, t_Version } from 'src/app/services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { DidDataRetrievalService, t_DataIdentifier, t_DataPoint } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GlobalService } from 'src/app/services/helper/global-helper';
import { IconHelper } from 'src/app/services/helper/icon-helper';
import { RoleService } from 'src/app/services/helper/role-handling-helper';
import { dataIdentifierEqual } from 'src/app/services/helper/did-handling-helper';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { Router } from '@angular/router';
import { compare } from 'semver';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';


type InfoData = {
  dataName: t_dataName;
  unit?: string;
  value?: number;
  type: string;
  trans: string;
  goodState?: number;
}

export type InfoDataSet = {
  dataPoints: InfoData[]
}

const diagConfig = 'RemoteDiagnosticsConfigInstalledVersion';


@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {

  public machinenumber: string = "";
  public line: string = '';
  public version_timestamp: string = "";
  public loading: boolean = false;
  public error: boolean = false;
  public errorMessage: string = "";
  public comissionEnabled: boolean = false;
  public swAlreadyLoaded: boolean = false;
  public fetchInterval: any;
  public loadingInfo: boolean = false;
  public ecuListSet: boolean = true;

  public infoDataset: InfoDataSet = JSON.parse(JSON.stringify(this.global.menuInfoset));
  private destroy$: Subject<void> = new Subject<void>();
  private destroyed: boolean = false;
  previousUrl: string = '';
  private lastActivity: number = 0;
  private diagConfigVersion: string = '';

  versions: t_Version[] = [];
  master_sw: string = '';

  defaultImage: string = '../../assets/images/machine/renderings/default.png';

  constructor(public iconHelper: IconHelper, private didConfigService: DidConfigService, private basicFunctionService: BasicMachineFunctionsService, private retrievalService: DidDataRetrievalService, public roleService: RoleService, private global: GlobalService, private translate: TranslateService, private apiGwConnection: GatewayConnectionService, private router: Router, public dialog: MatDialog){
    this.global.comission.subscribe({
      next: newValue => {
        this.comissionEnabled = newValue.enabled;
      }
    });
    this.global.machineInfos.subscribe({
      next: newValue => {
        this.line = newValue.line;
      }
    });
    this.global.machineNumber.subscribe({
      next: newValue => {this.machinenumber = newValue.number;}
    });
    this.global.swVersion.subscribe({
      next: newValue => {
        this.swAlreadyLoaded = newValue.loaded;
        if(this.swAlreadyLoaded){
          this.versions = newValue.versions;
        }
      }
    });
    this.global.previousUrl$.subscribe((previousUrl: string) => {
      this.previousUrl = previousUrl;      
    });
    this.global.lastActivity$.subscribe((lastActivity: number) => {
      this.lastActivity = lastActivity;
    });
    this.didConfigService.initService();
    this.basicFunctionService.initService(this.line);
  }

  ngOnInit(): void {
    // SW Version
    if(!this.swAlreadyLoaded) {
      this.loading = true;
      this.basicFunctionService.getExchangeToken().subscribe((res) => {
        let response = JSON.parse(JSON.stringify(res.body));
        this.basicFunctionService.getSWVersions(response.access_token,this.machinenumber).subscribe((resp) => {
          this.handleVersionResponse(resp);
        });
      });
    }
    // Basic Info
    this.loadingInfo = true;
    if(this.previousUrl != '' && this.previousUrl != '/') {
      if(this.previousUrl.includes('operation-times')){
        setTimeout(()=> {this.initBasisInfos();}, 1000);
      } else {
        this.initBasisInfos();
      }
    } else {
      this.ecuListSet = false;
      this.setupServices().then(() => {
        this.ecuListSet = true;
        this.initBasisInfos(); 
        /*if(this.swAlreadyLoaded) {
          this.checkDiagConfig().then(() => {
            this.initBasisInfos();    
          });
        } else {
          this.initBasisInfos();
        }*/
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.fetchInterval?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.retrievalService.stopSendQueryAliveState();
    this.retrievalService.stopDataQuery();
    this.retrievalService.cleanDataPoints();
    this.infoDataset.dataPoints.forEach((datapoint) => {
      if(datapoint.dataName.identifier != undefined) {
        this.didConfigService.removeDidFromQueryList(datapoint.dataName.identifier).subscribe();
      }
    });
  }

  private isUserActive(timeoutMs: number) {
    return (
      !document.hidden || document.hasFocus() || Date.now() - this.lastActivity < timeoutMs
    )
  }

  public switchMachine(){
    this.apiGwConnection.disconnectFromGateway().subscribe();
    this.global.machineNumber.next({ number: '', id: '', series: ''});
    this.global.machineInfos.next({line: '', name:'', engine: ''});
    this.global.variables.next({ connected: false});
    this.global.swVersion.next({loaded: false, versions: []});
    this.global.comission.next({enabled: false});
    this.router.navigateByUrl("");
  }

  private checkForSWVersions() {
    this.loading = true;
    this.basicFunctionService.getExchangeToken().subscribe((res) => {
      let response = JSON.parse(JSON.stringify(res.body));
      this.basicFunctionService.getSWVersions(response.access_token,this.machinenumber).subscribe((resp) => {
        this.handleVersionResponse(resp);        
      });
    });
  }

  private checkDiagConfig(): Promise<void> {
    return new Promise((resolve) => {
      this.retrievalService.initService();
      let identifier = this.didConfigService.getDataIdentifierOfDataName(diagConfig);
      this.didConfigService.postNewDidQueryList([identifier]).subscribe({
        complete: () => {
          this.retrievalService.startDataQuery(2000);
          this.fetchDiagConfig(identifier).then(() => {
            this.compareVersions().then(() => {
              resolve();
            });
          });
        },
        error: (err) => {
          console.error(err);
          resolve();
        }
      });
    });
  }

  private fetchDiagConfig(identifier: t_DataIdentifier): Promise<void> {
    return new Promise((resolve) => {
      let sub = interval(1000).pipe(
        switchMap(() => this.retrievalService.getRawDidsDetails([identifier])),
        filter((resp) => {
          let data = resp.body;
          let timestamp = data![0].data![data![0].data!.length-1].timestamp;
          return new Date(timestamp).getTime() !== -1;
        }),
        take(1),
        timeout(10000)
        /*takeWhile((resp) => {
          let data = resp.body;
          let timestamp = data![0].data![data![0].data!.length-1].timestamp;
          console.log(timestamp, new Date(timestamp).getTime(), typeof timestamp, typeof new Date(timestamp).getTime());
          
          return new Date(timestamp).getTime() === -1;
        },true)*/
      ).subscribe({
        next: (res) => {
          if(res.body != null) {
            let data = res.body;
            if(data[0].data!.length > 0) {
              let str = data[0].data![data[0].data!.length-1].value;
              this.diagConfigVersion = Object.values(str).map(c => String.fromCharCode(c)).join('').replace(/\0.*$/, ''); 
            }
          }
          sub.unsubscribe();
          this.didConfigService.removeDidFromQueryList(identifier).subscribe();
          this.retrievalService.stopSendQueryAliveState();
          this.retrievalService.stopDataQuery();
          this.retrievalService.cleanDataPoints();
          setTimeout(()=> {
            resolve();
          },1000) 
        },
        error: (error) => {
          console.error(error);
          sub.unsubscribe();
          this.didConfigService.removeDidFromQueryList(identifier).subscribe();
          this.retrievalService.stopSendQueryAliveState();
          this.retrievalService.stopDataQuery();
          this.retrievalService.cleanDataPoints();
          resolve();
        } 
      });
    });
  }

  private compareVersions(): Promise<void> {
    return new Promise((resolve) => {
      if(this.diagConfigVersion != this.master_sw && this.machinenumber != '') {
        this.dialog.open(DiagConfigDialog, {
          width: '50%',
          data: [this.diagConfigVersion, this.master_sw],
          disableClose: true
        });
      }
      resolve();
      /*this.findHighestVersion().then((version) => {
        if(this.diagConfigVersion != version) {
          this.dialog.open(DiagConfigDialog, {
            width: '50%',
            data: [this.diagConfigVersion, version],
            disableClose: true
          });
        }
        resolve();
      });*/
    });
  }

  private findHighestVersion(): Promise<string> {
    return new Promise((resolve) => {
      let validVersions = this.versions.filter(v => {
        try {
          compare(v.version.replace('-', '.'), "0.0.0");
          return true;
        } catch {
          return false;
        }
      });
      let highestEcu = validVersions.reduce((max, current) => compare(current.version.replace('-', '.'), max.version.replace('-', '.')) > 0 ? current : max);     
      //let highestEcu = this.versions.reduce((max, current) => compare(current.version.replace('-', '.'), max.version.replace('-', '.')) > 0 ? current : max);     
      resolve(highestEcu.version);
    });
  }

  private setupServices(): Promise<void> {
    return new Promise((resolve) => {
      this.didConfigService.loadEcuList().then(() => {
        this.didConfigService.ecuList!.forEach((ecu) => {
          this.didConfigService.setEcuDidMapping(ecu.ecu_id).then(() => {
              resolve();
          });
        });
      });
    });
  }

  private initBasisInfos() {
    if(!this.destroyed){
      this.retrievalService.initService();
      this.getDataIdentifiersToDataNamesFromConfig();
      this.postDataIdentifierToQueryToBackend().then(()=> {
        if(this.destroyed){
          this.ngOnDestroy();
        }
        this.fetchBasisInfo();
      });
    }
  }

  getDataIdentifiersToDataNamesFromConfig() {
    if(!this.destroyed){
      this.infoDataset.dataPoints.forEach((infoDataPoint) => {
        infoDataPoint.dataName.identifier = this.didConfigService.getDataIdentifierOfDataName(infoDataPoint.dataName.name);
        infoDataPoint.dataName.didInformation = this.didConfigService.getDidInformation(
          infoDataPoint.dataName.identifier
        );
      });
      this.infoDataset.dataPoints = this.infoDataset.dataPoints
        .map((dataPoint) => dataPoint)
        .filter((dataPoint) => dataPoint.dataName.identifier?.ecu_id !== undefined); 
      if(this.destroyed){
        this.ngOnDestroy();
      }
    }       
  }

  private postDataIdentifierToQueryToBackend(): Promise<void> {
    return new Promise((resolve) => {
      let dataIdentifierList: t_DataIdentifier[] = this.infoDataset.dataPoints
        .map((dataPoint) => dataPoint.dataName.identifier)
        .filter((identifier) => identifier?.ecu_id !== undefined) as t_DataIdentifier[];
      this.didConfigService.postNewDidQueryList(dataIdentifierList).subscribe({
        complete: () => {
          resolve();
        },
      });
    });
  }
  

  fetchBasisInfo() {
    if(!this.destroyed) {
      this.retrievalService.startDataQuery(1000);
      this.fetchInterval = interval(3000).pipe(takeUntil(this.destroy$)).subscribe(()=> {
        this.fetchData();
        if(!this.swAlreadyLoaded && !this.loading){
          this.versions = [];
          this.checkForSWVersions();
        }
      });
    }
  }

  fetchData() {
    this.retrievalService.getAllDidsValues().subscribe((res) => {
      let response: t_DataPoint[] = JSON.parse(JSON.stringify(res.body));
      if (response != null) {
        this.processRawData(response); 
      }
    });
  }

  private processRawData(rawData: t_DataPoint[]) {
    rawData.forEach((dataPoint) => {
      if (!dataPoint.data || new Date(dataPoint.data.timestamp).getTime() < 0) {
        return;
      }
      let indexInDataset: number = this.findIndexForIdentifierDataset(dataPoint.identifier);
      /*let normalizedValue: number = this.normalizedValue(
          dataPoint.data.value[0],
          this.infoDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
          this.infoDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
        );
      this.infoDataset.dataPoints[indexInDataset].value = normalizedValue;*/
      if(this.infoDataset.dataPoints[indexInDataset].dataName.didInformation?.unit == 'mV') {
        let volt = dataPoint.data.value[0]/1000;
        let normalizedValue: number = this.normalizedValue(
          volt,
          this.infoDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
          this.infoDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
        );
        this.infoDataset.dataPoints[indexInDataset].value = normalizedValue;
      } else {
        let normalizedValue: number = this.normalizedValue(
          dataPoint.data.value[0],
          this.infoDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
          this.infoDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
        );
        this.infoDataset.dataPoints[indexInDataset].value = normalizedValue;
      }
    });
    this.loadingInfo = false; 
  }
  
  private findIndexForIdentifierDataset(searchIdentifier: t_DataIdentifier): number {
    let index: number = this.infoDataset.dataPoints.findIndex((datapoint) => {
      if (datapoint.dataName.identifier) {
        return dataIdentifierEqual(datapoint.dataName.identifier, searchIdentifier);
      } else {
        return false;
      }
    });
    return index;
  }
  
  private normalizedValue(value: number, factor: number, offset: number) {
    if(factor == 0) {
      factor = 1;
    }
    let res: number = (value * factor) + offset;
    res = parseFloat(res.toFixed(1));
    return res;
    }

  handleVersionResponse(resp: HttpResponse<Object>){
    if(resp.status == 200) {
      this.convertVersions(resp.body);
    } else if(resp.status == 400) {
      this.translate.stream("ERROR.SW_VERSION.MACHINE").subscribe((res) => { this.errorMessage = res;});
      this.error = true;
      this.loading = false;
    } else if(resp.status == 403) {
      this.translate.stream("ERROR.SW_VERSION.AUTHORIZATION").subscribe((res) => { this.errorMessage = res;});
      this.error = true;
      this.loading = false;
    } else if(resp.status == 404) {
      if(resp.body == 'machine') {
        this.translate.stream("ERROR.SW_VERSION.MACHINE").subscribe((res) => { this.errorMessage = res;});
      } else if(resp.body == 'software') {
        this.translate.stream("ERROR.SW_VERSION.SOFTWARE").subscribe((res) => { this.errorMessage = res;});
      }
      this.error = true;
      this.loading = false;
    } else if(resp.status == 500) {
      this.translate.stream("ERROR.SW_VERSION.GENERAL").subscribe((res) => { this.errorMessage = res;});
      this.error = true;
      this.loading = false;
    }
  }

  convertVersions(json: any) {
    for(let i in json) {
      if(i === "timestamp"){
        this.version_timestamp = json[i];
      } else if(i.includes("ECU_")){
        let ecu = i.substring(i.indexOf('_')+1);
        if (ecu === 'Master') {
          this.translate.stream("MENU.VERSIONS.MASTER").subscribe((res) => { ecu = res;});
          this.master_sw = json[i];
        } else if(ecu === 'Slave_1') {
          this.translate.stream("MENU.VERSIONS.SLAVE_1").subscribe((res) => { ecu = res;});
        } else if(ecu === 'Slave_2') {
          this.translate.stream("MENU.VERSIONS.SLAVE_2").subscribe((res) => { ecu = res;});
        } else if(ecu === 'UnderCarriage') {
          this.translate.stream("MENU.VERSIONS.UNDERCARRIAGE").subscribe((res) => { ecu = res;});
        } else if(ecu === 'Engine') {
          this.translate.stream("MENU.VERSIONS.ENIGNE").subscribe((res) => { ecu = res;});
        } else if(ecu === 'Cabine') {
          this.translate.stream("MENU.VERSIONS.CABINE").subscribe((res) => { ecu = res;});
        } else if(ecu === 'HV_Slave') {
          this.translate.stream("MENU.VERSIONS.HV_SLAVE").subscribe((res) => { ecu = res;});
        } else if(ecu === 'Radio') {
          this.translate.stream("MENU.VERSIONS.RADIO").subscribe((res) => { ecu = res;});
        } else if(ecu === 'HVAC') {
          this.translate.stream("MENU.VERSIONS.Klimasteuergerät").subscribe((res) => { ecu = res;});
        }
        this.versions.push({name: ecu, version: json[i]});
      } else if(i.includes('telematics')){
        let ecu = 'Telematic'
        this.translate.stream("MENU.VERSIONS.TELEMATIC").subscribe((res) => { ecu = res;});
        this.versions.push({name: ecu, version: json[i]});
      } else if(i.includes('DISPLAY')){
        let ecu = 'Display';
        //ecu = this.translate.instant("MENU.VERSIONS.DISPLAY");
        this.translate.stream("MENU.VERSIONS.DISPLAY").subscribe((res) => { ecu = res;});
        this.versions.push({name: ecu, version: json[i]});
      }
    }
    this.loading = false;
    this.global.swVersion.next({loaded: true, versions: this.versions});
    this.swAlreadyLoaded = true;
  }

  updateUrl(event: any){
    event.target.src = "../../assets/images/machine/renderings/default.png";
  }

  public quickstart() {
    window.open('../../assets/quickstart.pdf', '_blank');
  }


}

// Dialog
@Component({
  selector: 'diag-config-dialog',
  templateUrl: 'diagconfig/diag-config-dialog.html',
  styleUrls: ['./diagconfig/diag-config-dialog.scss']
})
export class DiagConfigDialog implements OnInit{

  public diagConfigVersion: string;
  public ecuVersion: string;

  private machinenumber :string = '';
  private user: string = '';

  public closeEnabled: boolean = false;
  public timeElapsed: number = 100;

  constructor(@Inject(MAT_DIALOG_DATA) public data: [string, string], private global: GlobalService, private translate: TranslateService){
    this.diagConfigVersion = data[0];
    if(this.diagConfigVersion === '') {
      this.diagConfigVersion = this.translate.instant("MENU.DIAG_CONFIG.NONE");
    }
    this.ecuVersion = data[1]
    this.global.machineNumber.subscribe({
      next: newValue => {this.machinenumber = newValue.number;}
    });
    this.global.user.subscribe({
      next: newValue => {this.user = newValue.name;},
    });
  }

  ngOnInit(): void {
    let sub = interval(1000).subscribe(() => {
      this.timeElapsed = this.timeElapsed -20;
      if(this.timeElapsed <= 0) {
        this.closeEnabled = true;
        sub.unsubscribe();
      }
    });
  }

  public sendEmail() {
    let flattenString = `Machine: ${this.machinenumber}%0ADiagConfig installiert: ${this.diagConfigVersion}%0AECU Version: ${this.ecuVersion}%0AUser: ${this.user}`;

    window.open(
      'mailto:remotediagnose@sennebogen.de?Subject=Diagnostics Config Abweichung&body=Content:%0A%0A%0A%0A====================================%0A'
      + flattenString +
      '%0A====================================%0A'
    );
  }
}
